import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Settings as SettingsIcon,
  Info,
  Users,
  TriangleAlert,
  Check,
  UserPlus,
  Trash2,
  Archive,
  Loader2,
  ShieldAlert,
  X,
  Database,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem } from "@/components/brand/Hero";
import { Pill } from "@/components/brand/Pill";
import { Mantra, MantraAccent } from "@/components/brand/Card";
import { SettingsShell, FieldCard, Field } from "@/components/brand/SettingsShell";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select as RadixSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useProject,
  useUpdateProject,
  useDeleteProject,
  useProjectMembers,
  useAssignableUsers,
  useAddProjectMember,
  useRevokeProjectMember,
} from "@/hooks/useApi";
import { useCurrentUser } from "@/hooks/useAuth";
import { roleLabel, roleTone } from "@/lib/roles";
import { errorDetailOr } from "@/lib/api-error";
import { avatarColor, initialsOf } from "@/lib/avatar";
import { formatDate } from "@/lib/datetime";
import { cn } from "@/lib/utils";
import type { ProjectMember } from "@/api/project-members";
import type { UpdateProjectRequest } from "@/types/api";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ProjectApiKeysCard } from "@/components/projects/settings/ProjectApiKeysCard";
import {
  ProjectSourceCard,
  changedSourceFields,
  sourceDraftOf,
  draftDemoPackages,
  type SettingsSourceDraft,
} from "@/components/projects/settings/ProjectSourceCard";
import { windowMonthsIsValid } from "@/lib/projectSource";

/**
 * Âncora do aviso de nome vazio — o `aria-describedby` do campo aponta para
 * cá. Constante e não literal repetido: dois lugares escrevendo o mesmo id à
 * mão divergem no primeiro ajuste, e a ligação se perde **em silêncio** (o
 * atributo continua lá, apontando para nada).
 */
const NAME_ERROR_ID = "project-name-error";

/**
 * Dois nomes de projeto são "o mesmo" para a confirmação de exclusão.
 *
 * Compara pelo texto **renderizado**, não pelo valor cru, porque é o renderizado
 * que a pessoa lê e copia: o HTML colapsa espaço em branco, então `"Alpha "` e
 * `"Alpha  Beta"` aparecem na tela como `Alpha` e `Alpha Beta`. Comparando cru,
 * quem digita exatamente o que vê nunca habilita o botão, e o projeto fica sem
 * como ser excluído por esta tela — sem nada explicando por quê.
 *
 * Afrouxar aqui não enfraquece a trava: ela existe para provar que a pessoa sabe
 * **qual** projeto está excluindo, não para ser uma assinatura. A guarda que
 * importa (nome vazio nunca confirma) está em `canDelete`, não aqui.
 */
function sameProjectName(typed: string, expected: string) {
  const normalize = (value: string) => value.trim().replace(/\s+/g, " ");
  return normalize(typed) === normalize(expected);
}

export default function ProjectSettings() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  // Sem `isError` de propósito — ver a guarda antes do `return`: erro com dado
  // carregado é refetch falho, não projeto ausente, e não pode derrubar a tela.
  const {
    data: project,
    isLoading: projectLoading,
    refetch: refetchProject,
  } = useProject(projectId);
  // Mesma queryKey do card abaixo: o TanStack deduplica, então a KPI sai de graça.
  const { data: members } = useProjectMembers(projectId);
  const updateProject = useUpdateProject(projectId ?? "");
  const deleteProject = useDeleteProject();

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const isArchived = project?.status === "archived";
  const expectedConfirm = project?.name ?? "";
  const canDelete =
    expectedConfirm.trim().length > 0 && sameProjectName(deleteConfirmText, expectedConfirm);

  /**
   * O rascunho do card General, e **só dele** — nenhum dos outros campos que a
   * tela exibia tinha coluna atrás (#430).
   *
   * `PATCH /api/projects/{id}` aceita quatro campos (`UpdateProjectRequest`):
   * `name` e `description`, que estão aqui; `status`, que é o Archive do Danger
   * zone e viaja sozinho; e `current_phase`, que saiu da tela — `ProjectDetail`
   * lê `pipeline.computed_phase` **antes** dela, então editar a fase aqui não
   * mudaria o header do projeto assim que qualquer agente completasse.
   *
   * ⚠️ **Sem reset por efeito.** Esta tela tem um segundo escritor: o Archive
   * faz `PATCH { status }` e invalida `["project", id]`. Um `useEffect` que
   * zerasse o rascunho a cada objeto novo de `project` apagaria o que a pessoa
   * tivesse digitado quando esse refetch voltasse — foi o defeito removido do
   * `AdminSettings`. Nada se perde sem ele: os campos renderizam o rascunho por
   * cima do servidor, então o intocado segue o refetch sozinho.
   *
   * ⚠️ **"Por cima" é `"x" in draft`, não `draft.x ?? persistido`.** A descrição
   * se limpa de propósito. Limpar grava `""` no rascunho, e `??` trata `""` como
   * "não tocado": o merge cairia de volta no valor do servidor, o texto antigo
   * reapareceria com o Save habilitado, e salvar mandaria o valor antigo de
   * volta — o esvaziamento nunca aconteceria.
   */
  const [draft, setDraft] = useState<UpdateProjectRequest>({});

  const merged = useMemo(
    () => ({
      name: "name" in draft ? (draft.name ?? "") : (project?.name ?? ""),
      description:
        "description" in draft ? (draft.description ?? "") : (project?.description ?? ""),
    }),
    [draft, project],
  );

  /**
   * #1784: origem, modo e janela têm rascunho à parte, no formato do formulário (a janela em
   * texto, para a caixa aceitar o meio da digitação), e salvam no mesmo par do rodapé. `null` é
   * "não tocado": o card mostra o que o projeto tem.
   */
  const [sourceDraft, setSourceDraft] = useState<SettingsSourceDraft | null>(null);
  const shownSource = sourceDraft ?? (project ? sourceDraftOf(project) : null);
  const sourceChanges =
    project && shownSource ? changedSourceFields(project, shownSource) : {};
  const sourceDirty = Object.keys(sourceChanges).length > 0;
  const windowValid = !shownSource || windowMonthsIsValid(Number(shownSource.window_months));
  // Pacote fora do namespace do cliente a api recusa (422); o card mostra quais e o salvar espera.
  const demoValid = !shownSource || draftDemoPackages(shownSource).invalid.length === 0;

  const dirty = Object.keys(draft).length > 0 || sourceDirty;

  /**
   * `name` é `min_length=1` no backend. Sem esta guarda o clique vira um `422`
   * do FastAPI, cujo `detail` é uma **lista** de erros de validação — e o
   * `errorDetailOr` descarta lista de propósito (renderizar daria
   * `[object Object]`). O resultado seria a frase genérica na tela e nenhuma
   * pista de qual campo recusou.
   */
  const nameValid = merged.name.trim().length > 0;
  const canSave = dirty && nameValid && windowValid && demoValid && !updateProject.isPending;

  /**
   * Se **este** save está em voo — não "alguma escrita no projeto".
   *
   * Archive e Save dividem o mesmo `useUpdateProject`, então `isPending` sobe
   * também quando alguém arquiva. Ele continua governando o `disabled`, e isso
   * é correto: duas escritas no mesmo recurso não podem correr juntas. Mas o
   * spinner é sinal **desta** ação, e movido por `isPending` ele aparecia no
   * botão de Save durante o Archive, afirmando que o rascunho estava sendo
   * gravado quando nada dele tinha saído da tela.
   */
  const [saving, setSaving] = useState(false);

  const handleArchive = async () => {
    if (!projectId) return;
    try {
      await updateProject.mutateAsync({ status: "archived" });
      // "Runs stopped" saiu: nenhum run para. `archived` aparece uma única vez
      // nos quatro serviços Python — dentro do regex de `UpdateProjectRequest`
      // —, e nada lê a coluna para decidir coisa alguma. O "recoverable" fica:
      // `handleRestore` (`Projects.tsx:843`) devolve o projeto com
      // `PATCH { status: "draft" }`.
      toast({
        title: t("Project archived"),
        description: t("Recoverable from the Projects list."),
      });
      setArchiveOpen(false);
    } catch (err) {
      // `errorDetailOr`, não `String(err)`: ver o docstring do `handleSave`,
      // logo abaixo. O `message` de um erro do axios é "Request failed with
      // status code 403" — inglês, cru e sem informação para quem lê.
      toast({
        variant: "destructive",
        title: t("Archive failed"),
        description: errorDetailOr(err, t("Could not archive the project.")),
      });
    }
  };

  const handleDelete = async () => {
    if (!projectId || !canDelete) return;
    try {
      await deleteProject.mutateAsync(projectId);
      toast({ title: t("Project deleted"), description: t("All runs, artifacts and memories purged.") });
      navigate("/projects");
    } catch (err) {
      /**
       * Mesmo motivo do Archive acima — e aqui o caso mais comum não é falha de
       * rede: a aba de settings para em `manager` (`project-nav.ts`) e o
       * `DELETE` exige `admin` (`projects.py`, admin de propósito). Um manager
       * chega até aqui, digita o nome certo, e o backend recusa com `403`. Com
       * `String(err)` ele lia "Request failed with status code 403" e não tinha
       * como saber que o problema é o papel dele.
       */
      toast({
        variant: "destructive",
        title: t("Delete failed"),
        description: errorDetailOr(err, t("Could not delete the project.")),
      });
    }
  };

  /**
   * Descarrega o rascunho inteiro num `PATCH` só.
   *
   * O corpo leva **apenas as chaves tocadas** — o backend monta o `SET`
   * dinamicamente e recusa com `400` quando não vem nenhuma, que é o caso que o
   * `canSave` já barra antes de sair daqui.
   *
   * `errorDetailOr`, não `errorDetail`: esta tela tem frase traduzida pronta
   * para quando o backend não manda `detail`, e o `message` de um erro do axios
   * seria "Request failed with status code 400" — inglês, cru e sem informação
   * nenhuma para quem lê.
   */
  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      /**
       * O nome viaja trimado; a descrição, não.
       *
       * ⚠️ **E o trim é aqui, não no `onChange`.** Trimar a cada tecla faria o
       * React reescrever a caixa a cada keystroke, e o espaço **no meio** nunca
       * se formaria — "Migração" nunca viraria "Migração Sul". Mesmo ciclo que
       * comia o ponto decimal no cap de orçamento do `AdminSettings` (`:172`).
       *
       * ⚠️ **O motivo do trim mudou, e o antigo não vale mais.** Ele nasceu para
       * impedir que um nome gravado como "Alpha " travasse a exclusão, porque
       * `canDelete` comparava contra o valor cru. Esse defeito foi corrigido na
       * própria comparação (`sameProjectName`, no topo do arquivo), que é onde a
       * invariante mora — o trim aqui nunca destravaria as linhas já gravadas.
       *
       * O que faz ele continuar valendo é outra coisa: o valor gravado é o que
       * aparece no hero, na lista de projetos e no que alguém cola num chamado.
       * Espaço invisível nas pontas não deve chegar ao banco.
       *
       * A descrição fica como foi escrita: recuo e quebra de linha no fim de um
       * texto longo são escolha de quem escreveu, e nada a compara com entrada
       * digitada.
       */
      const sent = { ...draft };
      const sentSource = sourceDraft;
      const payload = { ...sent, ...sourceChanges };
      if (typeof payload.name === "string") payload.name = payload.name.trim();
      await updateProject.mutateAsync(payload);
      // Mesma regra do rascunho do General, logo abaixo: só sai o que ainda é o que partiu.
      setSourceDraft((current) => (current === sentSource ? null : current));
      /**
       * Limpa **o que foi salvo**, não o rascunho inteiro.
       *
       * `sent` é uma foto tirada antes do `await`, e os campos seguem editáveis
       * durante o save — de propósito (ver o bloco dos controlados, no fim do
       * arquivo: campo travado é o que a #430 tirou desta tela). Então dá tempo
       * de digitar enquanto o PATCH está no ar, e um `setDraft({})` cego apaga
       * isso junto, com o toast verde por cima. Seria a mesma perda silenciosa
       * que o `catch` logo abaixo se dá o trabalho de evitar — o caminho de
       * sucesso não pode fazer o que o de erro recusa.
       *
       * ⚠️ **Não é "remover as chaves enviadas".** Reeditar o **mesmo** campo
       * durante o voo perderia a edição nova pelo mesmo motivo. A chave só sai
       * se o rascunho dela ainda for idêntico ao que partiu.
       *
       * ⚠️ **A comparação é contra `sent`, não contra `payload`.**
       * `payload.name` já vem trimado; comparar com ele nunca casaria com o que
       * está na caixa, e a chave ficaria presa no rascunho para sempre —
       * `dirty` verdadeiro depois de um save bem-sucedido.
       */
      setDraft((current) => {
        const next = { ...current };
        for (const key of Object.keys(sent) as (keyof UpdateProjectRequest)[]) {
          if (next[key] === sent[key]) delete next[key];
        }
        return next;
      });
      toast({
        title: t("Settings saved"),
        description: t("The project has been updated."),
      });
    } catch (err) {
      // O rascunho **fica**. Save que falha e ainda apaga o que a pessoa
      // digitou transforma um erro recuperável em trabalho perdido.
      toast({
        variant: "destructive",
        title: t("Save failed"),
        description: errorDetailOr(err, t("Could not save the project.")),
      });
    } finally {
      // `finally`, não o fim do `try`: o ramo de erro também precisa devolver o
      // botão ao estado clicável, senão uma falha de rede deixa o Save girando
      // para sempre e o rascunho preso na tela.
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraft({});
    setSourceDraft(null);
  };

  /**
   * Nada de formulário antes do projeto chegar — e os dois estados são
   * diferentes, por isso duas saídas.
   *
   * ⚠️ **Depois de todos os hooks acima, e é obrigatório que seja.** Saída
   * antecipada antes de um `useState`/`useMemo` muda a ordem dos hooks entre
   * renderizações e o React quebra.
   *
   * Sem estas guardas, `project` `undefined` fazia `merged.name` ser `""`,
   * `nameValid` ser falso, e a tela acusar em vermelho que **o projeto não tem
   * nome** — quando o que faltou foi o projeto. Pior: o formulário continuava
   * editável, e digitar um nome liberava um `PATCH` que renomeava um registro
   * cujos dados reais nunca foram lidos.
   *
   * Não é hipotético. O `ProjectRouteGuard` redireciona em `403/404/422`
   * (`UNREACHABLE_STATUSES`) e **deixa passar 5xx e erro de rede** — nesses
   * casos a tela monta e a query nunca resolve.
   *
   * ⚠️ **O critério é a ausência de projeto, e `isError` não entra nele.** No
   * TanStack v5 erro não implica ausência de dado: o reducer de `error` preserva
   * `data`, e o observer expõe `isRefetchError = isError && hasData` justamente
   * para separar os dois casos. Dois gatilhos de rotina produzem erro **com**
   * dado carregado — o `invalidateQueries` que roda depois de todo save
   * (`useApi.ts:156`) e o `refetchOnWindowFocus` que o `queryClient.ts` deixa
   * ligado com `staleTime: 30_000`. Barrar por erro trocava o formulário
   * recém-salvo por "não foi possível carregar / nada foi alterado", duas
   * frases falsas ao mesmo tempo.
   *
   * A regra já estava escrita para esta mesma situação em
   * `AdminSettings.tsx:641`: "`isLoading` guarda o botão, e `isError` **não**:
   * falha de rede não pode trancar o admin fora da própria ação."
   *
   * A falha de primeira carga continua coberta: ali não há `data` nenhum. E a
   * query desabilitada (sem `projectId`) cai no mesmo lugar, que é o certo —
   * não é erro, e também não há projeto.
   */
  if (projectLoading) return <SettingsSkeleton />;
  if (!project) return <ProjectUnavailable onRetry={() => refetchProject()} />;

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<SettingsIcon />}
        // Segue `merged`, não `project`: renomear é a edição mais visível desta
        // tela, e ver o título acompanhar o que se digita é o que faz o campo
        // parecer vivo antes do save.
        overline={`${(merged.name || "PROJECT").toUpperCase()} · ${t("CONFIGURATION")}`}
        title={t("Settings")}
        meta={
          /* `miniBadge="Auto-saved"` saiu daqui: era a afirmação mais direta da
             tela e a mais falsa — nada salvava. Agora salva, e salva por botão.

             "Workspace: exed · Americas" saiu junto: o payload de projeto não
             traz nome de workspace, e "Americas" não é região de coisa nenhuma.
             Antes dele já tinha saído "Owner: Alana Garcia", porque **não existe
             dono de projeto** — não há papel por projeto (S-056 decisão 1), só
             atribuição, e o papel que vale é o global.

             Sobra "Created", que é a única das três que tem coluna atrás. */
          <HeroMetaItem k={t("Created")} v={formatDate(project?.created_at)} />
        }
        kpis={[
          {
            label: t("Members"),
            value: String(members?.length ?? 0),
            sub:
              (members?.length ?? 0) === 0
                ? t("nobody assigned — admins only")
                : t("assigned to this project"),
          },
          // "Default Agent · SKU Mapper", "Retention · 7 years" e "Region ·
          // EU-west-1" saíram: nenhum dos três tem coluna, job ou configuração
          // atrás, e os dois últimos ainda nomeavam os cards que esta issue
          // removeu — KPI apontando para seção que não existe é o mesmo beco
          // que a SIMP.4 fechou na nav, só que fora do array que o teste cobria.
          {
            label: t("Status"),
            // ⚠️ Valor cru do banco, em maiúsculas — e **não** porque falte
            // tradução. Os quatro rótulos existem nos três bundles ("Active",
            // "Draft", "Archived", "Completed"); o que não existe é um lugar só
            // decidindo o rótulo de status, como `roleLabel` faz para papel.
            //
            // Fica cru por consistência: `Projects.tsx` também renderiza o
            // status sem `t()` (`capitalize(p.status)` na célula Status do
            // card), e traduzir só aqui criaria a
            // divergência inversa — esta tela em português e a lista em inglês.
            // Fechar as duas é uma PR própria, fora do #430.
            value: (project?.status ?? "—").toUpperCase(),
            sub: isArchived ? t("out of the active list") : t("in the active list"),
          },
        ]}
      />

      <div className="p-8">
        <SettingsShell
          groups={[
            {
              header: t("PROJECT"),
              items: [
                { id: "general", label: t("General"), icon: <Info /> },
                { id: "members-access", label: t("Members & access"), icon: <Users /> },
                // #1784: origem, modo e janela, e as chaves do ex-kit (#1783).
                { id: "source-extraction", label: t("Source and extraction"), icon: <Database /> },
                { id: "api-keys", label: t("API keys"), icon: <KeyRound /> },
              ],
            },
            // O grupo PLATFORM saiu inteiro junto com o card `Data & retention`,
            // seu único item: header de grupo sem item renderiza um rótulo e uma
            // divisória com nada embaixo.
            {
              header: "",
              items: [
                { id: "danger-zone", label: t("Danger zone"), icon: <TriangleAlert />, danger: true },
              ],
            },
          ]}
          // `id` estável, não o rótulo traduzido — achado de review em
          // `SettingsShell.tsx` (a âncora não pode mudar com o idioma).
          defaultActive="general"
        >
          <FieldCard
            id="general"
            icon={<Info />}
            title={t("General")}
            sub={t("Project identity and scope visible across myGROW.")}
            // Fixo em "Synced", este pill mentia no instante em que alguém
            // digitasse — e com o formulário ligado isso passou a acontecer.
            pill={dirty ? <Pill tone="warn">{t("Unsaved changes")}</Pill> : undefined}
          >
            <Field label={t("Project name")} hint={t("Shown in breadcrumbs and exports.")}>
              {/* Os limites vêm do `UpdateProjectRequest` (`requests.py:66`).
                  Sem eles, estourar o tamanho devolve um 422 cujo `detail` é
                  uma lista — que o `errorDetailOr` descarta —, e sobra a frase
                  genérica sem dizer qual campo recusou. */}
              {/* `aria-label` porque o rótulo do `Field` é um `<div>`, sem
                  `htmlFor` — sem isto o leitor de tela anuncia só "caixa de
                  texto". A correção estrutural (dar `id`/`htmlFor` ao `Field`)
                  consertaria o `AdminSettings` junto e fica para PR própria. */}
              <ControlledInput
                value={merged.name}
                placeholder={t("Untitled project")}
                maxLength={255}
                ariaLabel={t("Project name")}
                ariaDescribedBy={nameValid ? undefined : NAME_ERROR_ID}
                onChange={(v) => setDraft((d) => ({ ...d, name: v }))}
              />
              {/* O Save desabilita sozinho com nome vazio; sem esta linha ele
                  desabilitaria sem dizer por quê — e sem o `id` acima, o aviso
                  ficaria solto na página, nunca lido junto do campo. */}
              {!nameValid && (
                <p id={NAME_ERROR_ID} className="text-[11px] text-[#b02a31] mt-1.5">
                  {t("A project needs a name. Saving stays blocked until you type one.")}
                </p>
              )}
            </Field>
            <Field
              label={t("Description")}
              hint={t("What this project covers. Everyone assigned to it reads this.")}
            >
              <ControlledTextarea
                value={merged.description}
                placeholder={t("No description yet.")}
                maxLength={2000}
                ariaLabel={t("Description")}
                onChange={(v) => setDraft((d) => ({ ...d, description: v }))}
              />
            </Field>
            {/* Somente-leitura aparece como **valor**, não como input cinza
                (#430, item 5 do escopo): campo travado convida ao clique e não
                explica por que recusa. É também o que se cola num chamado. */}
            <Field label={t("Project ID")} hint={t("Not editable. Quote it in support tickets.")}>
              <div className="h-9 flex items-center font-mono text-xs text-[#596b85] break-all">
                {project?.id ?? "—"}
              </div>
            </Field>
          </FieldCard>

          <MembersCard id="members-access" projectId={projectId} />

          {shownSource && (
            <ProjectSourceCard
              id="source-extraction"
              value={shownSource}
              onChange={(next) => setSourceDraft({ ...shownSource, ...next })}
              dirty={sourceDirty}
              windowInvalid={!windowValid}
            />
          )}

          <ProjectApiKeysCard id="api-keys" projectId={project.id} />

          {/* ------------------------------------------------------------ */}
          {/* Saíram daqui: Agent defaults, Notifications e Data & retention. */}
          {/*                                                                */}
          {/* Doze campos, nenhuma coluna atrás de nenhum deles (#430, epic   */}
          {/* #422). Modelo primário, fallback, cache de prompt, canal do     */}
          {/* Teams, digest semanal, região e política de retenção não       */}
          {/* existem no banco nem em serviço nenhum; o cap de orçamento e o  */}
          {/* gate de aprovação existem, mas são **do workspace** e já têm    */}
          {/* tela em `/admin/settings`, então repeti-los aqui prometia um    */}
          {/* ajuste por projeto que ninguém consegue fazer.                  */}
          {/*                                                                */}
          {/* Mesmo corte da SIMP.4 (#425) em `/admin/settings` com Branding  */}
          {/* e API keys: o que não tem nada atrás sai da tela e do nav, não  */}
          {/* ganha rótulo de "em breve". Se algum virar escopo, entra como   */}
          {/* issue — não como card inerte.                                   */}
          {/* ------------------------------------------------------------ */}

          <FieldCard
            id="danger-zone"
            icon={<TriangleAlert />}
            title={t("Danger zone")}
            sub={t("Irreversible actions. Owners only.")}
            pill={<Pill tone="danger">{t("Owner access")}</Pill>}
            danger
          >
            <Field
              label={t("Archive project")}
              // O ramo do já-arquivado continua como estava: é verdade, e o
              // `handleRestore` da lista de projetos grava exatamente `draft`.
              // O outro dizia "Read-only · billing stops": arquivar não trava
              // escrita nenhuma — este próprio formulário continua salvando — e
              // não existe cobrança no produto.
              hint={
                isArchived
                  ? t("Already archived · unarchive returns it to draft.")
                  : t("Takes it out of the active list · recoverable.")
              }
            >
              <div className="text-right">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-[30px] text-xs gap-1.5"
                  onClick={() => setArchiveOpen(true)}
                  disabled={isArchived || updateProject.isPending}
                >
                  <Archive className="w-3.5 h-3.5" /> {isArchived ? t("Archived") : t("Archive")}
                </Button>
              </div>
            </Field>
            <Field
              label={t("Delete project")}
              hint={t("Purges all runs, artifacts, connections and memories immediately.")}
            >
              <div className="text-right">
                <Button
                  variant="danger"
                  size="sm"
                  className="h-[30px] text-xs gap-1.5"
                  onClick={() => {
                    setDeleteConfirmText("");
                    setDeleteOpen(true);
                  }}
                  disabled={deleteProject.isPending}
                >
                  <Trash2 className="w-3.5 h-3.5" /> {t("Delete permanently")}
                </Button>
              </div>
            </Field>
          </FieldCard>

          <div className="sticky bottom-0 pt-5 pb-2 flex justify-end gap-2.5 bg-gradient-to-b from-transparent to-[#f7f7f7]">
            {/* O único par de save/descarte da tela: o do hero saiu (#1042), e
                este acompanha a rolagem. Os dois travam por critérios diferentes.

                `!canSave` no save e **não** no descarte: com o nome apagado o
                Save trava, e descartar é justamente como se sai desse estado.
                Travar os dois pelo mesmo critério prenderia a pessoa — nem
                salva, nem desfaz.

                `|| saving` nos dois: desfazer não cancela um PATCH que já
                partiu. Sem isto, numa conexão lenta, clicar em Discard devolvia
                o campo ao valor antigo e um segundo depois o `setQueryData` do
                `useUpdateProject` escrevia o valor novo por cima, com toast
                verde confirmando a renomeação recém-desfeita. `saving` é
                transitório, então isto não recria o beco do parágrafo acima. */}
            <Button variant="outline" onClick={handleDiscard} disabled={!dirty || saving}>
              {t("Discard")}
            </Button>
            <Button variant="action" className="gap-2" onClick={handleSave} disabled={!canSave}>
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )}{" "}
              {t("Save changes")}
            </Button>
          </div>
        </SettingsShell>

        <Mantra>
          {t("CONFIGURABLE")} · <MantraAccent>{t("AUDITABLE")}</MantraAccent> · CLEAN CORE
        </Mantra>
      </div>

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Archive this project?")}</AlertDialogTitle>
            <AlertDialogDescription>
              <SentenceWithName
                sentence={t(
                  "Takes {{name}} out of the active Projects list. Runs, artifacts and connections keep working, and the project stays editable; you can restore it later from the Projects list.",
                  { name: expectedConfirm },
                )}
                needle={expectedConfirm}
              >
                <strong>{expectedConfirm}</strong>
              </SentenceWithName>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateProject.isPending}>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} disabled={updateProject.isPending}>
              {updateProject.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("Archive project")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="max-w-[560px] p-0 overflow-hidden gap-0 border-[#e8ebf0] rounded-[14px] shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
          <AlertDialogHeader className="relative bg-[#08254c] text-white px-[22px] pt-[18px] pb-4 flex flex-row gap-3.5 items-start space-y-0 sm:text-left overflow-hidden">
            <span
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 95% -10%, rgba(255,200,61,0.20), transparent 55%)",
              }}
            />
            <div
              className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-[#ffc83d]"
              style={{ background: "rgba(255,200,61,0.14)", border: "1px solid rgba(255,200,61,0.40)" }}
            >
              <TriangleAlert className="w-5 h-5" />
            </div>
            <div className="relative flex-1 min-w-0">
              <AlertDialogTitle className="font-brand font-bold text-[17px] text-white m-0 mb-1.5 tracking-[-0.01em] leading-[1.3]">
                <SentenceWithName
                  sentence={t("Delete “{{name}}”?", { name: expectedConfirm })}
                  needle={`“${expectedConfirm}”`}
                >
                  <span className="text-[#ffc83d]">“{expectedConfirm}”</span>
                </SentenceWithName>
              </AlertDialogTitle>
              {/* Duas frases, duas chaves — a fronteira é de frase, então o
                  `<strong>` envolve a segunda inteira e não precisa de split. */}
              <AlertDialogDescription className="text-[13px] text-white/70 leading-[1.55] m-0">
                {t(
                  "This will permanently remove the project, all run history, generated artifacts, and SAP connections.",
                )}{" "}
                <strong className="text-white font-semibold">{t("This cannot be undone.")}</strong>
              </AlertDialogDescription>
            </div>
            <AlertDialogCancel
              className="relative ml-auto mt-0 h-7 w-7 p-0 rounded-md bg-transparent border-0 text-white/55 hover:bg-white/10 hover:text-white shrink-0"
              aria-label={t("Close")}
            >
              <X className="w-3.5 h-3.5" />
            </AlertDialogCancel>
          </AlertDialogHeader>

          <div className="px-[22px] pt-4 pb-[18px] bg-[#fafbfc] border-t border-[#f2f4f7]">
            <div
              className="flex gap-2.5 items-start p-3 rounded-lg text-[12.5px] leading-[1.5]"
              style={{
                background: "#fff6db",
                border: "1px solid rgba(255,200,61,0.55)",
                color: "#6a4a00",
              }}
            >
              <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={2.25} />
              <span>
                {t("Runs, artifacts, SAP connections, and chat threads on this project will be deleted alongside it.")}
              </span>
            </div>

            {/* Só com rascunho pendente. O diálogo pede o nome **salvo**, e está
                certo — o rascunho não existe no servidor. Mas o overline do hero
                e o campo de nome seguem `merged.name`, então quem renomeia sem
                salvar vê a tela inteira dizendo um nome e o diálogo pedindo
                outro. Sem rascunho não há divergência, e o aviso não aparece. */}
            {dirty && (
              <div className="flex gap-2.5 items-start p-3 mt-2.5 rounded-lg text-[12.5px] leading-[1.5] bg-[rgba(242,115,37,0.08)] border border-[rgba(242,115,37,0.25)] text-[#c25500]">
                <TriangleAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                <span>
                  {t(
                    "You have unsaved changes. The name asked below is the saved one, not what the screen shows — and the pending edits go away with the project.",
                  )}
                </span>
              </div>
            )}
            <div className="mt-3.5">
              <Label htmlFor="confirm-delete" className="block text-xs text-[#08254c] mb-1.5 font-semibold">
                {/* Uma chave só. O "Type" ficava fora do `t()` e o "to confirm"
                    dentro — daí a frase costurada de dois idiomas. */}
                <SentenceWithName
                  sentence={t("Type {{name}} to confirm", { name: expectedConfirm })}
                  needle={expectedConfirm}
                >
                  <span
                    className="font-mono font-bold text-[#08254c] px-1.5 rounded-[3px]"
                    style={{ background: "rgba(255,200,61,0.25)" }}
                  >
                    {expectedConfirm}
                  </span>
                </SentenceWithName>
              </Label>
              <Input
                id="confirm-delete"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t("Project name…")}
                autoComplete="off"
                className="h-[38px] font-mono text-[13px] text-[#08254c] border-[#d4dae3] focus-visible:ring-[#08254c]/20 focus-visible:ring-[3px] focus-visible:border-[#08254c] focus-visible:ring-offset-0"
              />
              {deleteConfirmText.length > 0 && !canDelete && (
                <div className="text-[11px] text-[#08254c] font-semibold mt-1.5 font-mono tracking-[0.04em]">
                  {/* Chave própria, e não a compartilhada com `Projects.tsx`:
                      lá a comparação continua estrita, então a frase antiga
                      ainda descreve aquela tela. Aqui ela ficou falsa pela
                      metade quando o `sameProjectName` passou a normalizar
                      espaço — e mandava quem errou a caixa caçar um espaço que
                      seria perdoado de qualquer forma. */}
                  {t("Case matters. Extra spaces are ignored.")}
                </div>
              )}
            </div>
          </div>

          <AlertDialogFooter className="px-[22px] py-3.5 gap-2 border-t border-[#f2f4f7] bg-white flex-row justify-end items-center sm:space-x-0">
            <span className="mr-auto font-mono text-[10.5px] text-[#7b8799] tracking-[0.10em] uppercase flex items-center">
              {t("Irreversible · admin only")}
            </span>
            <AlertDialogCancel
              disabled={deleteProject.isPending}
              className="h-9 mt-0 px-3.5 rounded-md border border-[#d4dae3] bg-white text-[#08254c] text-[13px] font-semibold hover:bg-[#f7f7f7] hover:border-[#08254c]"
            >
              {t("Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={!canDelete || deleteProject.isPending}
              className="h-9 px-3.5 rounded-md bg-[#08254c] border border-[#08254c] text-white text-[13px] font-semibold gap-1.5 shadow-[0_4px_12px_rgba(8,37,76,0.25)] hover:bg-[#0d356e] hover:border-[#0d356e]"
            >
              {deleteProject.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#ffc83d]" />
              ) : (
                <Trash2 className="w-3.5 h-3.5 text-[#ffc83d]" />
              )}
              {t("Delete permanently")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Membros do projeto — a atribuição que existia no backend e em lugar nenhum
// na tela.
//
// Os três endpoints são da SEC.4 e **nada os chamava**, enquanto o filtro por
// atribuição já estava ligado em `GET /api/projects`
// (`is_membership_scoped`, `projects.py:77`). Consequência: todo não-admin via
// uma lista de projetos vazia, para sempre, e não havia como consertar pela
// aplicação — só por `INSERT` no banco.
//
// O card que estava aqui era ficção: pessoas fixas no código ("Alana Garcia",
// "Ricardo Matos"), o vocabulário morto Owner/Editor/Viewer, e um subtítulo
// afirmando "SSO enforced via Okta" numa instalação que só fala OIDC com a
// Microsoft.
// ---------------------------------------------------------------------------



/**
 * Membros do projeto.
 *
 * **Não há gate de papel aqui dentro, e é de propósito.** Conceder e revogar é de
 * `manager` para cima (R4/R12) — o consultor faz quase tudo dentro do projeto,
 * menos decidir quem trabalha nele —, mas esse corte já está na **rota**:
 * `PROJECT_TABS` declara a aba de Settings como `manager`, e o
 * `ProjectRouteGuard` redireciona qualquer papel abaixo disso antes desta tela
 * montar. Quem chega aqui pode mexer.
 *
 * O card já teve um `canManage = hasMinRole(me?.role, "manager")` escondendo os
 * botões. Era sempre `true`, e o custo não era o ramo morto: eram os comentários
 * ao lado dele descrevendo "um consultor que abre esta tela", que ensinam ao
 * próximo leitor um gate que não existe (achado C3 do review). Se um dia a aba
 * descer para `consultant`, o teste em `project-members-card.test.tsx` fica
 * vermelho apontando para cá.
 */
function MembersCard({ id, projectId }: { id?: string; projectId: string | undefined }) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [addOpen, setAddOpen] = useState(false);

  const { data: members, isLoading } = useProjectMembers(projectId);
  // Só quando o diálogo abre: a lista é do tenant inteiro e serve a um campo que
  // a maior parte das visitas a esta tela nunca abre. Ver `useAssignableUsers`.
  const { data: assignable } = useAssignableUsers(projectId, { enabled: addOpen });
  const addMember = useAddProjectMember(projectId);
  const revokeMember = useRevokeProjectMember(projectId);
  const [picked, setPicked] = useState<string>("");
  const [pendingRevoke, setPendingRevoke] = useState<ProjectMember | null>(null);

  /**
   * O membro que o diálogo **está descrevendo** — não o mesmo que o pendente.
   *
   * `pendingRevoke` controla o `open`, e volta a `null` no instante em que o
   * diálogo começa a fechar (o `AlertDialogAction` é o `Dialog.Close` do Radix e
   * dispara o `onOpenChange(false)` no clique). O `Presence` segue renderizando o
   * conteúdo pelos 200ms da animação de saída, e nesse render o `?? ""` do título
   * entrava em ação: o diálogo saía de cena escrevendo "Remove from this
   * project?" **sem nome**, e o corpo caía no ramo genérico.
   *
   * Não é limpo no fechamento, de propósito: guarda o último membro aberto para
   * os textos lerem. Mesma correção do `describedAction` em `AdminTeam`, onde o
   * sintoma era pior — lá a saída citava outra ação destrutiva.
   */
  const [describedMember, setDescribedMember] = useState<ProjectMember | null>(null);

  /** Abre a confirmação. Os dois estados andam juntos na abertura, nunca no fim. */
  const askRevokeConfirmation = (member: ProjectMember) => {
    setDescribedMember(member);
    setPendingRevoke(member);
  };

  /**
   * Candidatos que ainda não estão no projeto.
   *
   * O `POST` é idempotente (`ON CONFLICT ... DO UPDATE`), então reatribuir alguém
   * não quebraria nada — mas oferecer no picker quem já aparece na lista logo
   * acima faz a tela parecer que não sabe o próprio estado.
   */
  const candidates = useMemo(() => {
    const alreadyMembers = new Set((members ?? []).map((m) => m.user_id));
    return (assignable ?? []).filter((u) => !alreadyMembers.has(u.user_id));
  }, [assignable, members]);

  const handleAdd = async () => {
    if (!picked) return;
    try {
      await addMember.mutateAsync(picked);
      toast({ title: t("Access granted") });
      setAddOpen(false);
      setPicked("");
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("Could not grant access."),
        description: errorDetailOr(err, ""),
      });
    }
  };

  const handleRevoke = async () => {
    if (!pendingRevoke) return;
    const wasMyself = pendingRevoke.user_id === me?.id;
    try {
      await revokeMember.mutateAsync(pendingRevoke.user_id);
      toast({ title: t("Access revoked") });
      // Sem `setPendingRevoke(null)`: o `AlertDialogAction` é o `Dialog.Close`
      // do Radix e já fechou o diálogo no clique, disparando o `onOpenChange`.
      // Mesma remoção feita no `runPending` do `AdminTeam`.
      /**
       * Revogou a própria participação: sair daqui não é cortesia, é o único
       * final que funciona. O manager não é isento de atribuição (só o admin é),
       * então a partir deste ponto toda leitura desta tela responde `404` — e
       * ficar parado nela deixa a pessoa olhando erros de uma ação que deu certo.
       *
       * O admin passa por aqui e **não** deveria sair: ele continua alcançando o
       * projeto pelo papel. Mas o diálogo dele já diz isso ("não tira o acesso"),
       * e mandá-lo para a lista de projetos — onde o projeto continua — é uma
       * volta inofensiva, não um beco. Não vale um ramo a mais.
       */
      if (wasMyself) navigate("/projects");
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("Could not revoke access."),
        description: errorDetailOr(err, ""),
      });
    }
  };

  const total = members?.length ?? 0;

  return (
    <FieldCard
      id={id}
      icon={<Users />}
      title={t("Members & access")}
      /**
       * "Atribuídas", não "alcançam". A contagem nunca foi o número de pessoas
       * que enxergam o projeto: `is_membership_scoped` isenta o admin
       * (`project_access.py:44`), então **todo** admin do workspace alcança este
       * projeto apareça ele nesta lista ou não. Dizer "N alcançam" era falso nos
       * dois sentidos — para mais e para menos.
       */
      sub={
        isLoading
          ? t("Loading…")
          : `${t("{{n}} people assigned.", { n: total })} ${t(
              "Administrators reach every project, listed here or not.",
            )}`
      }
      right={
        <Button
          variant="outline"
          size="sm"
          className="h-[30px] text-xs gap-1.5"
          onClick={() => setAddOpen(true)}
        >
          <UserPlus className="w-3.5 h-3.5" /> {t("Add member")}
        </Button>
      }
    >
      {/* O aviso que a tela precisa dar antes de alguém estranhar a lista vazia:
          um projeto sem membros é invisível para todo mundo que não é admin. */}
      {!isLoading && total === 0 && (
        <div className="flex items-start gap-2 text-xs text-[#c25500] bg-[rgba(242,115,37,0.08)] border border-[rgba(242,115,37,0.25)] rounded px-2.5 py-2">
          <ShieldAlert className="w-3.5 h-3.5 mt-px shrink-0" />
          <span>
            {t(
              "Nobody is assigned yet. Until someone is, this project only shows up for administrators.",
            )}
          </span>
        </div>
      )}

      <div className="flex flex-col">
        {(members ?? []).map((m) => (
          <div
            key={m.user_id}
            className="flex justify-between items-center py-2.5 border-b border-[#f2f4f7] last:border-b-0"
          >
            <div className="flex items-center gap-3">
              <div
                className="w-[34px] h-[34px] rounded-full flex items-center justify-center font-brand font-bold text-xs text-white"
                style={{ background: avatarColor(m.user_id) }}
              >
                {initialsOf(m.name)}
              </div>
              <div>
                <div className="font-semibold text-[#08254c] text-[13px]">
                  {m.name}
                  {m.user_id === me?.id && (
                    <span className="ml-1.5 font-normal text-[11px] text-[#7b8799]">
                      ({t("you")})
                    </span>
                  )}
                </div>
                <div className="font-mono text-[11px] text-[#7b8799]">{m.email}</div>
                {/* O acesso do admin não vem daqui. Sem esta linha, remover a
                    pessoa carimba `revoked_at` no banco e não muda nada na tela
                    dela — foi exatamente o que se viu no teste manual. */}
                {m.role === "admin" && (
                  <div className="text-[11px] text-[#c25500] mt-0.5">
                    {t("Reaches this project even without this assignment.")}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2.5 items-center">
              {/* Papel **global**, não por projeto: não existe papel por projeto
                  (S-056 decisão 1). Ele aparece porque é o que decide o que a
                  pessoa consegue fazer lá dentro. */}
              <Pill tone={roleTone(m.role)}>{roleLabel(m.role, t)}</Pill>
              <button
                type="button"
                aria-label={t("Remove {{name}} from the project", { name: m.name })}
                onClick={() => askRevokeConfirmation(m)}
                className="p-1 rounded text-[#7b8799] hover:text-[#d4353c] hover:bg-[rgba(212,53,60,0.08)] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Add someone to this project")}</DialogTitle>
            <DialogDescription>
              {t(
                "They get to see the project and work in it with whatever their workspace role allows.",
              )}
            </DialogDescription>
          </DialogHeader>
          {candidates.length === 0 ? (
            <p className="text-[13px] text-[#596b85]">
              {t("Everyone in the workspace is already on this project.")}
            </p>
          ) : (
            <RadixSelect value={picked} onValueChange={setPicked}>
              <SelectTrigger aria-label={t("Person")}>
                <SelectValue placeholder={t("Pick a person")} />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name} · {u.email} · {roleLabel(u.role, t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </RadixSelect>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button
              variant="action"
              onClick={handleAdd}
              disabled={!picked || addMember.isPending}
            >
              {addMember.isPending ? t("Adding…") : t("Add member")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingRevoke !== null}
        onOpenChange={(open) => !open && setPendingRevoke(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("Remove {{name}} from this project?", { name: describedMember?.name ?? "" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {/* Três frases, e a ordem importa.
                  1. **Admin primeiro**, inclusive quando é a própria conta: para
                     ele as outras duas seriam mentira. O acesso dele vem do papel,
                     não da linha, então remover não tira nada hoje.
                  2. A própria conta: perde o projeto da lista na hora.
                  3. Qualquer outro: revogar derruba o grant do Code Companion na
                     mesma transação (`_REVOKE_CC_GRANTS`). Sem dizer isso, um
                     manager acha que a pessoa só perde a tela e segue no terminal. */}
              {describedMember?.role === "admin"
                ? t(
                    "⚠️ This will not take their access away: administrators reach every project in the workspace, assigned or not. Removing the row only matters if they stop being an administrator.",
                  )
                : describedMember?.user_id === me?.id
                  ? t(
                      "⚠️ This is your own access. You lose the project from your list immediately, and getting back in depends on someone else adding you.",
                    )
                  : t(
                      "They lose the project immediately, and any Code Companion terminal access to it stops working. Reversible: add them back and the access returns.",
                    )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke}>{t("Remove")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FieldCard>
  );
}

// ---------------------------------------------------------------------------
// Campos controlados — o par que o `SettingsShell` não tem.
//
// O `SettingsShell` tinha `TextInput` e `Textarea` **não controlados por
// contrato** (`defaultValue`, estado no DOM, nenhum `onChange` para fora), feitos
// para cards de mockup e removidos na #1044 por falta de uso. Foram a causa raiz
// do defeito 1 da #430: a tela passava `value=` para um componente que só lia
// `defaultValue`, e o resultado era um campo que exibia o valor e não o
// reportava a ninguém.
//
// ⚠️ **Estes dois são quase idênticos aos de `AdminSettings.tsx:503`, e isso é
// dívida registrada, não um padrão a copiar.** A terceira tela com formulário
// que salvar deve promovê-los ao `SettingsShell` — não repetir a cópia. A
// extração não entrou nesta PR porque o `AdminSettings` tem três desses
// (`ControlledInput`, `ControlledSelect`, `ControlledSwitch`) e aqui bastam
// dois: mover só o que esta tela usa deixaria aquela meio migrada, e mover os
// três é refactor de uma tela que funciona dentro de uma PR que conserta outra.
//
// Duas diferenças deliberadas em relação às cópias de lá: sem `disabled` e sem
// `type`. Campo travado é justamente o que a #430 tirou desta tela — o que não
// se edita aqui aparece como valor, não como input cinza.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Os dois estados em que não há formulário para mostrar.
//
// Ficam locais em vez de virarem componentes compartilhados porque não há hoje
// um segundo consumidor: o repositório trata carregamento com `Skeleton` inline
// imitando o layout da própria tela (`ProjectDetail.tsx:28`,
// `ProjectCost.tsx:106`), e o único estado de erro com retry que existe
// (`CleanCoreError`, em `Architect.tsx:376`) também é local — e sem `t()`, que
// este não copia.
// ---------------------------------------------------------------------------

/** Imita a forma da tela: hero, nav à esquerda, três cards à direita. */
function SettingsSkeleton() {
  return (
    <div className="-m-6">
      <Skeleton className="h-[220px] w-full rounded-none" />
      <div className="p-8">
        <div className="grid grid-cols-[220px_minmax(0,1fr)] gap-6 items-start">
          <Skeleton className="h-64" />
          <div className="flex flex-col gap-5">
            <Skeleton className="h-72" />
            <Skeleton className="h-56" />
            <Skeleton className="h-40" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * O projeto não chegou.
 *
 * Diz **o que não aconteceu** ("nada foi alterado"), porque a alternativa que
 * esta tela oferecia era pior que um erro: um formulário editável sobre um
 * registro que ninguém leu.
 */
function ProjectUnavailable({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="-m-6">
      <div className="p-8">
        <div className="mx-auto max-w-md bg-white border border-[#e8ebf0] rounded-xl px-6 py-10 flex flex-col items-center gap-3 text-center">
          <TriangleAlert className="w-6 h-6 text-[#b02a31]" />
          <h2 className="font-brand font-bold text-base text-[#08254c] m-0">
            {t("Could not load this project.")}
          </h2>
          <p className="text-[13px] text-[#596b85] m-0">
            {t("Nothing was changed. The settings stay closed until it loads.")}
          </p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t("Try again")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Uma frase traduzida, com o nome do projeto destacado no meio dela.
 *
 * Os dois diálogos do Danger zone nomeiam o projeto **dentro** da frase e o
 * destacam — dourado no título de excluir, mono com fundo dourado no campo de
 * confirmação. Costurar isso com JSX (`Delete <span>{nome}</span>?`) foi
 * exatamente o que produziu a tela bilíngue: só um pedaço passava por `t()`, e
 * o resto ficava em inglês em qualquer idioma ("Type teste3 **para
 * confirmar**").
 *
 * A saída canônica do react-i18next seria `<Trans>`, e ela está descartada por
 * um motivo concreto: `app-i18n-coverage.test.ts` varre `t("…")` por regex, e
 * chave declarada em `<Trans i18nKey>` escaparia da guarda — que existe
 * justamente porque chave faltando não gera erro nenhum, só uma tela meio em
 * inglês. Aqui a frase **inteira** é um `t()`, a guarda enxerga, e o destaque
 * sai de partir o resultado já traduzido.
 *
 * `indexOf` de string pura, não `RegExp`: nome de projeto com `(`, `.` ou `*`
 * é nome válido e não pode virar metacaractere. E `at === -1` devolve a frase
 * inteira sem destaque — se uma tradução futura não contiver o nome, o pior
 * caso é perder o realce, nunca perder o nome.
 */
function SentenceWithName({
  sentence,
  needle,
  children,
}: {
  sentence: string;
  needle: string;
  children: ReactNode;
}) {
  const at = needle ? sentence.indexOf(needle) : -1;
  if (at === -1) return <>{sentence}</>;
  return (
    <>
      {sentence.slice(0, at)}
      {children}
      {sentence.slice(at + needle.length)}
    </>
  );
}

function ControlledInput({
  value,
  placeholder,
  onChange,
  mono,
  style,
  maxLength,
  ariaLabel,
  ariaDescribedBy,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  mono?: boolean;
  style?: React.CSSProperties;
  maxLength?: number;
  ariaLabel: string;
  ariaDescribedBy?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "w-full h-9 px-3 border border-[#d4dae3] rounded-md bg-white text-[13px] text-[#08254c] font-[inherit]",
        "focus:outline-0 focus:border-[#ffc83d] focus:ring-[3px] focus:ring-[rgba(255,200,61,0.15)]",
        mono && "font-mono text-xs",
      )}
      style={style}
    />
  );
}

function ControlledTextarea({
  value,
  placeholder,
  onChange,
  maxLength,
  ariaLabel,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  maxLength?: number;
  ariaLabel: string;
}) {
  return (
    <textarea
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "w-full min-h-20 px-3 py-2.5 border border-[#d4dae3] rounded-md bg-white text-[13px] text-[#08254c] font-[inherit] resize-y",
        "focus:outline-0 focus:border-[#ffc83d] focus:ring-[3px] focus:ring-[rgba(255,200,61,0.15)]",
      )}
    />
  );
}
